import { useState, useEffect, useRef, useCallback } from 'react';

interface UseStickyOptions {
  offset?: number;
  rootMargin?: string;
}

export const useSticky = (options: UseStickyOptions = {}) => {
  const { offset = 64 } = options;
  const [isSticky, setIsSticky] = useState(false);
  const [elementHeight, setElementHeight] = useState(0);
  const elementRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!sentinelRef.current || !elementRef.current) return;

    const sentinelRect = sentinelRef.current.getBoundingClientRect();
    const shouldBeSticky = sentinelRect.top <= offset;
    
    if (shouldBeSticky && !isSticky) {
      const height = elementRef.current.offsetHeight;
      setElementHeight(height);
    }
    
    setIsSticky(shouldBeSticky);
  }, [offset, isSticky]);

  useEffect(() => {
    const handleScrollThrottled = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener('scroll', handleScrollThrottled, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [handleScroll]);

  return {
    isSticky,
    elementHeight,
    elementRef,
    sentinelRef,
    placeholderRef,
  };
}; 